"use client";

import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
// disable no-css-rule for swiper styles
import "swiper/css/bundle";
import { Autoplay } from "swiper/modules";
import { cn } from "utils";
import { Icon } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type { Question } from "../../Sender/QuestionItem";
import Translations from "@translate/components/CoPilot/CopilotAgent/CopilotSenderTabInput";
import { I18nUtil } from "@utils/i18n";
import { ChatStore } from "@/src/store";
import { useOpenChatModalWithClose } from "@/src/hooks";
import { SESSION_STORAGE_KEYS } from "@/src/config";
import { getRandomElements } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

type CopilotSenderTabInputProps = {
  /** 千人千面 chat tab 问题列表 */
  chatQuestion?: Question[];
  questions?: Question[];
  className?: string;
  sign?: string;
};

const Typewriter = ({ text, speed = 30 }: { text: string; speed?: number }) => {
  const [displayed, setDisplayed] = useState("");

  useEffect(() => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayed(text);
      return;
    }

    let isCancelled = false;

    const typeNext = (index = 0) => {
      if (isCancelled || index >= text.length) {
        return;
      }

      setDisplayed(text.slice(0, index + 1));

      setTimeout(() => {
        typeNext(index + 1);
      }, speed);
    };

    // 給一點延遲，讓 hydration 先穩定
    const timer = setTimeout(() => {
      typeNext();
    }, 0);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [text, speed]);

  return <span>{displayed}</span>;
};

export const CopilotSenderTabInput = (props?: CopilotSenderTabInputProps) => {
  const { questions, chatQuestion = [], className, sign = "" } = props || {};
  const { handleOpenChatModal } = useOpenChatModalWithClose();
  const [isloading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const handleChatClick = async (question?: Question) => {
    // 当前问题与随机推荐几个问题一起传递过去，如果没有当前问题，说明点击的是chat tab的输入框，这时候传递chat tab的问题列表过去
    ChatStore.showRecommendQuestion = true;
    const isQuestionClick = !!question;

    const randomQuestion =
      (isQuestionClick
        ? questions?.filter((q) => q.question !== question.question)
        : chatQuestion) || [];
    const recommendQuestion = isQuestionClick
      ? [
          { ...question, isHighlight: true },
          ...getRandomElements(randomQuestion),
        ]
      : getRandomElements(chatQuestion, 4);
    // 2026.05.25 逻辑更新：默认追加到上一个会话，不再区分新旧会话
    handleOpenChatModal({
      questions: recommendQuestion,
      sign,
    });
  };

  useEffect(() => {
    if (!questions?.length) {
      return;
    }
    const initPage = () => {
      const isFirstLoad = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.IS_FIRST_LOAD,
      );
      if (!isFirstLoad) {
        setIsFirstLoad(true);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_FIRST_LOAD, "1");
        setTimeout(() => {
          setIsFirstLoad(false);
        }, 2000);
      } else {
        setIsFirstLoad(false);
      }
      setLoading(false);
    };

    initPage();
  }, [questions?.length]);

  return (questions?.length ?? 0) > 0 && !isloading ? (
    <div
      className={cn(
        "flex w-full mb-3 px-3 py-2 h-[58px] gap-2 bg-background_solid_neutral_tertiary items-center justify-center border border-border_brand_transparent50 rounded-xl max-w-[calc(100%-24px)] mx-auto relative",
        className,
      )}
    >
      <div className="p-1 bg-content_brand_primary rounded-[5px] w-6 h-6 max-w-6 max-h-6 flex items-center justify-center">
        <Icon
          name="ai-magic-wand"
          size={16}
          className="text-content_inverse_primary"
        />
      </div>

      <div className="flex-1 flex items-center w-full h-full relative">
        {/* Typewriter 層 */}
        <div
          className={cn(
            "body_m w-full !leading-[16px] md:bg-gradient-to-r md:from-background_brand_overlay100 md:to-background_white_overlay100 md:bg-clip-text md:text-transparent text-content_brand_primary flex items-center whitespace-normal break-words !line-clamp-3 absolute transition-opacity duration-500",
            isFirstLoad ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          <Typewriter
            text={I18n.t("Feel free to ask me anything about investing...")}
            speed={24}
          />
        </div>

        {/* Swiper 層 */}
        <Swiper
          direction="vertical"
          slidesPerView={1}
          loop={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          allowTouchMove={false}
          style={{
            height: 58,
            margin: 0,
            width: "100%",
            opacity: isFirstLoad ? 0 : 1,
            pointerEvents: isFirstLoad ? "none" : "auto",
            transitionDuration: "3000ms",
            transitionProperty: "opacity",
          }}
          modules={[Autoplay]}
          onRealIndexChange={(swiper) => {
            const curQuestion = questions?.[swiper.realIndex];
            try {
              log.info(
                "[chath5][swiper] recommend questions",
                JSON.stringify(curQuestion),
              );
            } catch {
              log.info(
                "[chath5][swiper] recommend questions error",
                curQuestion,
              );
            }
          }}
        >
          {questions?.map((q, idx) => (
            <SwiperSlide
              key={`${q?.question}-${idx}`}
              className="!flex items-center w-full"
            >
              <div
                data-openreplay-label={`CopilotAgent · CopilotSenderTabInput · div | ${q?.question}`}
                className="body_m w-full !leading-[16px] md:bg-gradient-to-r md:from-background_brand_overlay100 md:to-background_white_overlay100 md:bg-clip-text md:text-transparent text-content_brand_primary flex items-center whitespace-normal break-words !line-clamp-3"
                onClick={() => handleChatClick(q)}
                data-tracking-scope={q.dataTrackingScope}
              >
                {q?.question}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  ) : null;
};
